import { Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider, useAuth } from './auth/AuthContext'
import { Auth0AuthProvider } from './auth/Auth0Auth'
import { LoginScreen } from './auth/LoginScreen'
import { ChoosePasswordScreen } from './auth/ChoosePasswordScreen'
import { ToastProvider } from './components/Toasts'
import { Shell } from './components/Shell'
import { Browse } from './screens/Browse'
import { RoleDetail } from './screens/RoleDetail'
import { NewRole } from './screens/NewRole'
import { BulkUpload } from './screens/BulkUpload'
import { MarketData } from './screens/MarketData'
import { AddSurvey } from './screens/AddSurvey'
import { SurveyList } from './screens/SurveyList'
import { MarketRecordScreen } from './screens/MarketRecord'
import { Companies } from './screens/Companies'
import { AddCompany } from './screens/AddCompany'
import { CompanyDetail } from './screens/CompanyDetail'
import { Benchmarking } from './screens/Benchmarking'
import { MatchAJob } from './screens/MatchAJob'
import { SalaryRanges } from './screens/SalaryRanges'
import { SalaryRangeDetail } from './screens/SalaryRangeDetail'
import { BenchmarkRun } from './screens/BenchmarkRun'
import { BenchmarkDetail } from './screens/BenchmarkDetail'
import type { ReactNode } from 'react'

/**
 * A manager's screen does not contain admin controls at all, not hidden ones
 * (FR-A3), so the admin-only routes are simply not mounted for them.
 */
function AdminOnly({ children }: { children: ReactNode }) {
  const { isAdmin } = useAuth()
  return isAdmin ? <>{children}</> : <Navigate to="/job-descriptions" replace />
}

function Authed() {
  const { me, loading, mustChoosePassword } = useAuth()

  if (loading) {
    return (
      <div className="login">
        <div className="spinner" aria-label="Loading" />
      </div>
    )
  }

  // The password they were given was right and is spent. They are one screen
  // away from being in, so this comes before the sign in form rather than
  // after a failure.
  if (mustChoosePassword) return <ChoosePasswordScreen />

  if (!me) return <LoginScreen />

  // A super admin has no library and no market data, so sending them to the
  // job descriptions screen would land them on an empty one. Their own screens
  // are the whole of what they can open.
  if (me.role === 'superadmin') {
    return (
      <Shell>
        <Routes>
          <Route path="/companies" element={<Companies />} />
          <Route path="/companies/new" element={<AddCompany />} />
          <Route path="/companies/:slug" element={<CompanyDetail />} />
          <Route path="*" element={<Navigate to="/companies" replace />} />
        </Routes>
      </Shell>
    )
  }

  return (
    <Shell>
      <Routes>
        <Route path="/" element={<Navigate to="/job-descriptions" replace />} />
        <Route path="/job-descriptions" element={<Browse />} />
        <Route
          path="/job-descriptions/new"
          element={
            <AdminOnly>
              <NewRole />
            </AdminOnly>
          }
        />
        <Route
          path="/job-descriptions/bulk"
          element={
            <AdminOnly>
              <BulkUpload />
            </AdminOnly>
          }
        />
        <Route path="/job-descriptions/:id" element={<RoleDetail />} />

        {/*
          Market data is admin only. Not a screen concern dressed up as one:
          survey data is licensed under terms about who may see it, so these
          routes are simply not mounted for a manager and every API route
          behind them refuses their token as well.
        */}
        <Route path="/benchmarking" element={<AdminOnly><Benchmarking /></AdminOnly>} />
        {/*
          Admin only for the same reason Benchmarking is: a match is made from
          survey data licensed under terms about who may see it, so the route
          is not mounted for a manager and every API route behind it refuses
          their token as well.
        */}
        <Route path="/match-a-job" element={<AdminOnly><MatchAJob /></AdminOnly>} />
        <Route path="/salary-ranges" element={<AdminOnly><SalaryRanges /></AdminOnly>} />
        <Route path="/salary-ranges/:jdId" element={<AdminOnly><SalaryRangeDetail /></AdminOnly>} />
        <Route path="/benchmarking/runs/:runId" element={<AdminOnly><BenchmarkRun /></AdminOnly>} />
        <Route path="/benchmarking/:jdId" element={<AdminOnly><BenchmarkDetail /></AdminOnly>} />
        <Route
          path="/market-data"
          element={<AdminOnly><MarketData /></AdminOnly>}
        />
        <Route
          path="/market-data/add"
          element={<AdminOnly><AddSurvey /></AdminOnly>}
        />
        <Route
          path="/market-data/surveys"
          element={<AdminOnly><SurveyList /></AdminOnly>}
        />
        <Route
          path="/market-data/:id"
          element={<AdminOnly><MarketRecordScreen /></AdminOnly>}
        />
        <Route path="*" element={<Navigate to="/job-descriptions" replace />} />
      </Routes>
    </Shell>
  )
}

/** `auth0`: signed in through Auth0 (STE-1012); otherwise the sign-in this build has always had. */
export function App({ auth0 = false }: { auth0?: boolean }) {
  const Provider = auth0 ? Auth0AuthProvider : AuthProvider
  return (
    <Provider>
      <ToastProvider>
        <Authed />
      </ToastProvider>
    </Provider>
  )
}
